import { AuthToken } from '@cobliteam/api-lib';
import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import Button from '@cobliteam/ui/components/Button';
import CopyButton from '@cobliteam/ui/components/CopyButton';
import Tag from '@cobliteam/ui/components/Tag';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import { format } from '@cobliteam/utils/date';
import { useState } from 'react';
import { copyJwtToken, copyToClipboard } from '../../lib/clipboard';
import DevToolsPanel from '../DevToolsPanel';

/**
 * Keep enough of the token to identify it without exposing a usable credential.
 * Below 20 characters the two slices overlap and the mask would print the whole
 * value, so a short token is hidden outright.
 */
const maskToken = (token: string) => (token.length < 20 ? '•'.repeat(24) : `${token.slice(0, 12)}${'•'.repeat(24)}${token.slice(-8)}`);

const UserData = () => {
  const [isTokenRevealed, setIsTokenRevealed] = useState(false);

  const { getSession, getUser } = useUser();
  const { fleet, authenticationToken } = getSession();
  const { id, name, email, role, creationDate } = getUser();

  const jwtToken = authenticationToken ?? localStorage.getItem(AuthToken.ACCESS_TOKEN);

  const handleCopyToken = () => {
    void copyJwtToken(jwtToken);
  };

  const rows: Array<{ label: string; value: string; copyable?: boolean }> = [
    { label: 'ID', value: id, copyable: true },
    { label: 'Nome', value: name },
    { label: 'E-mail', value: email, copyable: true },
    { label: 'Role', value: role },
    { label: 'Fleet ID', value: fleet.id, copyable: true },
    { label: 'Frota', value: fleet.name },
    // Previously fell back to `new Date()`, which showed today's date as if it
    // were the creation date. An em dash is honest about missing data.
    { label: 'Conta criada em', value: creationDate ? format(creationDate, 'dd/MM/yyyy') : '—' },
  ];

  return (
    <DevToolsPanel title='Meus dados' actions={role === 'cobler' ? <Tag color='danger' variant='surface' label='Cobler · permissões especiais' /> : undefined}>
      <dl className='divide-y divide-gray-4 overflow-hidden rounded-lg border border-gray-6'>
        {rows.map(({ label, value, copyable }) => (
          <div key={label} className='flex items-center justify-between gap-4 px-3 py-1.5'>
            <dt className='shrink-0'>
              <Typography variant='caption-xs' color='secondary'>
                {label}
              </Typography>
            </dt>
            <dd className='flex min-w-0 items-center gap-1'>
              <Typography variant='body' className='truncate font-mono text-xs'>
                {value}
              </Typography>
              {copyable && <CopyButton variant='ghost' copyToClipboard={copyToClipboard} textToCopy={value} buttonClassName='size-7' />}
            </dd>
          </div>
        ))}
      </dl>

      <div className='flex flex-col gap-2 rounded-lg border border-gray-6 p-3'>
        <div className='flex items-center justify-between gap-2'>
          <Typography variant='caption-xs' color='secondary'>
            JWT Token
          </Typography>
          <div className='flex items-center gap-1'>
            <Button
              variant='ghost'
              color='default'
              size='sm'
              startIcon={<Icon name={isTokenRevealed ? 'eye-off' : 'eye'} size='sm' />}
              disabled={!jwtToken}
              onClick={() => setIsTokenRevealed((revealed) => !revealed)}
            >
              {isTokenRevealed ? 'Ocultar' : 'Revelar'}
            </Button>
            <Button variant='ghost' color='default' size='sm' startIcon={<Icon name='copy' size='sm' />} disabled={!jwtToken} onClick={handleCopyToken}>
              Copiar
            </Button>
          </div>
        </div>
        {/* Masked by default: this panel gets screenshotted and screen-shared. */}
        <Typography variant='body' className='break-all font-mono text-gray-11 text-xs'>
          {jwtToken ? (isTokenRevealed ? jwtToken : maskToken(jwtToken)) : 'Token indisponível'}
        </Typography>
      </div>
    </DevToolsPanel>
  );
};

export default UserData;
